// Actions
import { ActionType } from "@ms/uno-actions/lib/local/ActionType";
// Constants
import { HttpStatus } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { MyDayUser, StoreEntityStatus, StoreTableNames } from "@ms/uno-constants/lib/local/StoreConstants";
import { CopilotWorkflowUnifiedState } from "@ms/uno-constants/lib/local/WorkflowConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Entity
import { ExistenceStatus } from "@ms/uno-entitycore/lib/local/EntityStatus";
import { BasicTable } from "@ms/uno-entitycore/lib/local/tables/BasicTable";
import { EntityTable } from "@ms/uno-entitycore/lib/local/tables/EntityTable";
import { TaskKind } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ITask";
import { TaskboardTaskFormattingCollection } from "@ms/uno-models/lib/local/client/legacyPlanner/task/taskFormatting/TaskboardTaskFormattingCollection";
import { StoreEntityStatusSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyStatus/StoreEntityStatusSerializationHandler";
import { TaskBoardFormatAssignedToSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyTasks/TaskBoardFormatAssignedToSerializationHandler";
import { TaskBoardFormatBucketSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyTasks/TaskBoardFormatBucketSerializationHandler";
import { TaskBoardFormatProgressSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyTasks/TaskBoardFormatProgressSerializationHandler";
import { TaskDetailsSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyTasks/TaskDetailsSerializationHandler";
import { TasksSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyTasks/TasksSerializationHandler";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { logUnreachableAction } from "../../../utilities/LoggingUtilities";
import { getStatusBasedOnResponse } from "../../../utilities/StatusUtilities";
import { upsertVersionedBaseline } from "../../../utilities/StoreUtilities";
export class BasicTaskStore extends EventPublishingStore {
    get name() {
        return "BasicTaskStore";
    }
    getTask(id, readBaseline) {
        const storeCopy = this.taskTable.get(id);
        const status = this.taskStatusTable.get(id) ?? StoreEntityStatus.Unfetched;
        const isFetching = this.isTaskFetchingInProgress[id] ?? false;
        return {
            data: (readBaseline ? storeCopy?.baseline : storeCopy?.value) ?? null,
            status: status,
            isFetching
        };
    }
    getProjectManagerWorkflowState(taskId) {
        return this.taskIdToPMWorkflowState[taskId];
    }
    getWorkflowStateForTask(taskId) {
        const task = this.taskTable.get(taskId)?.value;
        if (!task) {
            this.loggers.traceLogger.logTrace(0x1e20f24e /* tag_4ipjo */ , TraceLevel.Error, "task is null", {
                taskId
            });
            return CopilotWorkflowUnifiedState.Unassigned;
        }
        return this.getProjectManagerWorkflowState(taskId) ?? task.getWorkflowState();
    }
    getLoopPageUrlForTask(taskId) {
        const loopPageUrl = this.loopPageUrlForTaskTable.get(taskId);
        const status = this.loopPageUrlStatusTable.get(taskId) ?? StoreEntityStatus.Unfetched;
        return {
            data: loopPageUrl ?? null,
            status: status
        };
    }
    getInProgressUploadsForTask(taskId) {
        return this.inProgressUploadsByTask[taskId] ?? [];
    }
    getTaskDetails(id, readBaseline) {
        const taskDetails = this.taskDetailsTable.get(id);
        return (readBaseline ? taskDetails?.baseline : taskDetails?.value) ?? null;
    }
    getTasksForGoal(goalId) {
        return Object.values(this.taskTable.getByIndex({
            name: "goal",
            value: goalId
        })).map((task)=>task.value);
    }
    getTaskApprovalDetails(taskId) {
        const taskApprovalDetails = this.taskApprovalDetailsTable.get(taskId);
        const status = this.taskApprovalDetailsStatusTable.get(taskId) ?? StoreEntityStatus.Unfetched;
        return {
            data: taskApprovalDetails ?? null,
            status: status
        };
    }
    isTaskApprovalOperationInProgress(taskId) {
        return this.isApprovalOperationInProgress.get(taskId) ?? false;
    }
    getTaskboardTaskFormattingCollection(id, readBaseline) {
        const assignedTo = this.taskboardTaskFormatForAssignedToTable.get(id);
        const bucket = this.taskboardTaskFormatForBucketTable.get(id);
        const progress = this.taskboardTaskFormatForProgressTable.get(id);
        if (assignedTo == null && bucket == null && progress == null) {
            return null;
        }
        return TaskboardTaskFormattingCollection.builder.withPropertyBag({
            assignedBoardFormat: readBaseline ? assignedTo?.baseline : assignedTo?.value,
            bucketBoardFormat: readBaseline ? bucket?.baseline : bucket?.value,
            progressBoardFormat: readBaseline ? progress?.baseline : progress?.value
        }).build();
    }
    getTasksForPlan(planId) {
        const planTasks = Object.values(this.taskTable.getByIndex({
            name: "plan",
            value: planId
        }));
        const status = this.tasksInPlanStatusTable.get(planId) ?? StoreEntityStatus.Unfetched;
        const isFetching = this.isTaskInPlanFetchingInProgress[planId] ?? false;
        return {
            data: planTasks.map((task)=>task.value),
            status: status,
            isFetching
        };
    }
    getTasksForBucket(bucketId) {
        return Object.values(this.taskTable.getByIndex({
            name: "bucket",
            value: bucketId
        })).map((task)=>task.value);
    }
    getTasksAssignedToUser(userId) {
        const userTasks = Object.values(this.taskTable.getByIndex({
            name: "assignee",
            value: userId
        }));
        const status = this.tasksByAssigneeStatusTable.get(userId) ?? StoreEntityStatus.Unfetched;
        const isFetching = this.isTaskByAssigneeFetchingInProgress[userId] ?? false;
        return {
            data: userTasks.map((task)=>task.value),
            status: status,
            isFetching
        };
    }
    getMyDayTasks() {
        const myDayTasks = Object.values(this.taskTable.getByIndex({
            name: "myday",
            value: "true"
        }));
        const status = this.tasksInMyDayStatusTable.get(this.currentUserId) ?? StoreEntityStatus.Unfetched;
        const isFetching = this.isTaskInMyDayFetchingInProgress[this.currentUserId] ?? false;
        return {
            data: myDayTasks.map((task)=>task.value),
            status: status,
            isFetching
        };
    }
    isBulkEditInProgress() {
        return this.isBulkEditOngoing;
    }
    getPersistedTables() {
        return {
            [StoreTableNames.Tasks]: this.taskTable,
            [StoreTableNames.TaskDetails]: this.taskDetailsTable,
            [StoreTableNames.TaskboardTaskFormatForAssignedTo]: this.taskboardTaskFormatForAssignedToTable,
            [StoreTableNames.TaskboardTaskFormatForBucket]: this.taskboardTaskFormatForBucketTable,
            [StoreTableNames.TaskboardTaskFormatForProgress]: this.taskboardTaskFormatForProgressTable,
            [StoreTableNames.TasksInPlanStatus]: this.tasksInPlanStatusTable,
            [StoreTableNames.TasksByAssigneeStatus]: this.tasksByAssigneeStatusTable,
            [StoreTableNames.TasksInMyDayStatusTable]: this.tasksInMyDayStatusTable
        };
    }
    //#region handlers
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.FetchBasicTaskAction:
                storeChanged = this.handleFetchBasicTaskAction(action);
                break;
            case ActionType.FetchBasicTaskFailedAction:
                storeChanged = this.handleFetchBasicTaskFailedAction(action);
                break;
            case ActionType.FetchBasicTaskSucceededAction:
                storeChanged = this.handleFetchBasicTaskSucceededAction(action);
                break;
            case ActionType.FetchBasicTasksForPlanAction:
                storeChanged = this.handleFetchBasicTasksForPlanAction(action);
                break;
            case ActionType.FetchBasicTasksForPlanFailedAction:
                storeChanged = this.handleFetchBasicTasksForPlanFailedAction(action);
                break;
            case ActionType.FetchBasicTasksForPlanSucceededAction:
                storeChanged = this.handleFetchBasicTasksForPlanSucceededAction(action);
                break;
            case ActionType.FetchBasicTasksForTodoPlanFinalizedAction:
                storeChanged = this.handleFetchBasicTasksForTodoPlanFinalizedAction(action);
                break;
            case ActionType.FetchBasicTasksForAssigneeAction:
                storeChanged = this.handleFetchBasicTasksForAssigneeAction(action);
                break;
            case ActionType.FetchBasicTasksForAssigneeFailedAction:
                storeChanged = this.handleFetchBasicTasksForAssigneeFailedAction(action);
                break;
            case ActionType.FetchBasicTasksForAssigneeSucceededAction:
                storeChanged = this.handleFetchBasicTasksForAssigneeSucceededAction(action);
                break;
            case ActionType.FetchBasicTasksForMyDayAction:
                storeChanged = this.handleFetchBasicTasksForMyDayAction(action);
                break;
            case ActionType.FetchBasicTasksForMyDayFailedAction:
                storeChanged = this.handleFetchBasicTasksForMyDayFailedAction(action);
                break;
            case ActionType.FetchBasicTasksForMyDaySucceededAction:
                storeChanged = this.handleFetchBasicTasksForMyDaySucceededAction(action);
                break;
            case ActionType.CreateBasicTaskAction:
                storeChanged = this.handleCreateBasicTaskAction(action);
                break;
            case ActionType.CreateBasicTaskFailedAction:
                storeChanged = this.handleCreateBasicTaskFailedAction(action);
                break;
            case ActionType.CreateBasicTaskSucceededAction:
                storeChanged = this.handleCreateBasicTaskSucceededAction(action);
                break;
            case ActionType.DeltaSyncCreateBasicTaskAction:
                storeChanged = this.handleDeltaSyncCreateBasicTaskAction(action);
                break;
            case ActionType.DeleteBasicTaskAction:
                storeChanged = this.handleDeleteBasicTaskAction(action);
                break;
            case ActionType.DeleteBasicTaskFailedAction:
                storeChanged = this.handleDeleteBasicTaskFailedAction(action);
                break;
            case ActionType.DeleteBasicTaskSucceededAction:
                storeChanged = this.handleDeleteBasicTaskSucceededAction(action);
                break;
            case ActionType.DeltaSyncDeleteBasicTaskAction:
                storeChanged = this.handleDeltaSyncDeleteBasicTaskAction(action);
                break;
            case ActionType.UpdateBasicTaskAction:
                storeChanged = this.handleUpdateBasicTaskAction(action);
                break;
            case ActionType.UpdateBasicTaskFailedAction:
                storeChanged = this.handleUpdateBasicTaskFailedAction(action);
                break;
            case ActionType.UpdateBasicTaskSucceededAction:
                storeChanged = this.handleUpdateBasicTaskSucceededAction(action);
                break;
            case ActionType.DeltaSyncUpdateBasicTaskAction:
                storeChanged = this.handleDeltaSyncUpdateBasicTaskAction(action);
                break;
            case ActionType.UploadAttachmentForTaskAction:
                storeChanged = this.handleUploadAttachmentForTaskAction(action);
                break;
            case ActionType.UploadAttachmentForTaskFailedAction:
                storeChanged = this.handleUploadAttachmentForTaskFailedAction(action);
                break;
            case ActionType.UploadAttachmentForTaskSucceededAction:
                storeChanged = this.handleUploadAttachmentForTaskSucceededAction(action);
                break;
            case ActionType.FetchApprovalDetailsAction:
                storeChanged = this.handleFetchApprovalDetailsAction(action);
                break;
            case ActionType.FetchApprovalDetailsSucceededAction:
                storeChanged = this.handleFetchApprovalDetailsSucceededAction(action);
                break;
            case ActionType.FetchApprovalDetailsFailedAction:
                storeChanged = this.handleFetchApprovalDetailsFailedAction(action);
                break;
            case ActionType.CancelApprovalDetailsAction:
            case ActionType.UpdateApprovalDetailsAction:
            case ActionType.CreateApprovalDetailsAction:
                storeChanged = this.handleTaskApprovalDetailsAction(action);
                break;
            case ActionType.CancelApprovalDetailsFailedAction:
            case ActionType.UpdateApprovalDetailsFailedAction:
            case ActionType.CreateApprovalDetailsFailedAction:
                storeChanged = this.handleTaskApprovalDetailsActionFailure(action);
                break;
            case ActionType.CreateConversationThreadAction:
                storeChanged = this.handleCreateConversationThreadAction(action);
                break;
            case ActionType.CreateConversationThreadSucceededAction:
                storeChanged = this.handleCreateConversationThreadSucceededAction(action);
                break;
            case ActionType.CreateConversationThreadFailedAction:
                storeChanged = this.handleCreateConversationThreadFailedAction(action);
                break;
            case ActionType.CopilotWorkflowUnifiedStateUpdateAction:
                storeChanged = this.handleCopilotWorkflowUnifiedStateUpdateAction(action);
                break;
            case ActionType.UndeleteBasicTaskAction:
                storeChanged = this.handleUndeleteBasicTaskAction(action);
                break;
            case ActionType.UndeleteBasicTaskFailedAction:
                storeChanged = this.handleUndeleteBasicTaskFailedAction(action);
                break;
            case ActionType.UndeleteBasicTaskSucceededAction:
                storeChanged = this.handleUndeleteBasicTaskSucceededAction(action);
                break;
            case ActionType.FetchLoopPageUrlForTaskAction:
                storeChanged = this.handleFetchLoopPageUrlForTaskAction(action);
                break;
            case ActionType.FetchLoopPageUrlForTaskFailedAction:
                storeChanged = this.handleFetchLoopPageUrlForTaskFailedAction(action);
                break;
            case ActionType.FetchLoopPageUrlForTaskSucceededAction:
                storeChanged = this.handleFetchLoopPageUrlForTaskSucceededAction(action);
                break;
            case ActionType.DeleteLoopPageUrlForTaskAction:
                storeChanged = this.handleDeleteLoopPageUrlForTaskAction(action);
                break;
            case ActionType.UpdateBulkEditStatusAction:
                storeChanged = this.handleUpdateBulkEditStatusAction(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    handleFetchBasicTaskAction(action) {
        if (this.isTaskFetchingInProgress[action.taskId]) {
            return false;
        }
        this.isTaskFetchingInProgress[action.taskId] = true;
        return true;
    }
    handleFetchBasicTaskFailedAction(action) {
        let storeChanged = false;
        const { response, taskId } = action;
        if (this.isTaskFetchingInProgress[taskId]) {
            this.isTaskFetchingInProgress[taskId] = false;
            storeChanged = true;
        }
        // Update the status if appropriate
        if (response?.status === HttpStatus.NotFound || response?.status === HttpStatus.Forbidden) {
            return this.updateStatusBasedOnResponse(this.taskStatusTable, taskId, response) || storeChanged;
        } else {
            return this.taskStatusTable.set(taskId, StoreEntityStatus.UnableToFetch) || storeChanged;
        }
    }
    handleFetchBasicTaskSucceededAction(action) {
        const { task } = action.taskEntitySet;
        if (!task.createdBy) {
            const msg = `StoreException: Fetched Basic task has no createdBy [TaskId=${task.id}]`;
            this.loggers.traceLogger.logTrace(0x1e2495c1 /* tag_4jjxb */ , TraceLevel.Error, msg);
            throw new Error(msg);
        }
        return this.upsertTaskEntitySet(action.taskEntitySet);
    }
    handleFetchBasicTasksForAssigneeAction(action) {
        if (this.isTaskByAssigneeFetchingInProgress[action.userId]) {
            return false;
        }
        this.isTaskByAssigneeFetchingInProgress[action.userId] = true;
        return true;
    }
    handleFetchBasicTasksForAssigneeFailedAction(action) {
        let storeChanged = false;
        const { response, userId } = action;
        if (this.isTaskByAssigneeFetchingInProgress[userId]) {
            this.isTaskByAssigneeFetchingInProgress[userId] = false;
            storeChanged = true;
        }
        if (!response || response.status !== HttpStatus.NotFound && response.status !== HttpStatus.Forbidden) {
            storeChanged = this.tasksByAssigneeStatusTable.set(userId, StoreEntityStatus.UnableToFetch) || storeChanged;
            return storeChanged;
        }
        if (response.status === HttpStatus.NotFound) {
            storeChanged = this.tasksByAssigneeStatusTable.set(userId, StoreEntityStatus.NotFound) || storeChanged;
            return storeChanged;
        }
        if (response.status === HttpStatus.Forbidden) {
            storeChanged = this.tasksByAssigneeStatusTable.set(userId, StoreEntityStatus.Inaccessible) || storeChanged;
            return storeChanged;
        }
        return storeChanged;
    }
    handleFetchBasicTasksForAssigneeSucceededAction(action) {
        const { userId, taskEntitySets, isLastPage, accumulatedTaskIds } = action;
        let storeChanged = false;
        if (this.isTaskByAssigneeFetchingInProgress[userId]) {
            this.isTaskByAssigneeFetchingInProgress[userId] = false;
            storeChanged = true;
        }
        for (const taskEntitySet of taskEntitySets){
            // Insert the tasks into the store if non existent or update baseline if existent
            storeChanged = this.upsertTaskEntitySet(taskEntitySet) || storeChanged;
        }
        // Update the index status if there are any changes
        if (isLastPage) {
            storeChanged = this.deleteExtraneousTasks("assignee", userId, accumulatedTaskIds, true) || storeChanged;
            storeChanged = this.tasksByAssigneeStatusTable.set(userId, StoreEntityStatus.Present) || storeChanged;
        }
        return storeChanged;
    }
    handleFetchBasicTasksForMyDayAction(action) {
        if (this.isTaskInMyDayFetchingInProgress[this.currentUserId]) {
            return false;
        }
        this.isTaskInMyDayFetchingInProgress[this.currentUserId] = true;
        return true;
    }
    handleFetchBasicTasksForMyDayFailedAction(action) {
        let storeChanged = false;
        const { response } = action;
        if (this.isTaskInMyDayFetchingInProgress[this.currentUserId]) {
            this.isTaskInMyDayFetchingInProgress[this.currentUserId] = false;
            storeChanged = true;
        }
        // If no response or response is not 404 or 403, set status to UnableToFetch
        if (!response || response.status !== HttpStatus.NotFound && response.status !== HttpStatus.Forbidden) {
            storeChanged = this.tasksInMyDayStatusTable.set(this.currentUserId, StoreEntityStatus.UnableToFetch) || storeChanged;
            return storeChanged;
        }
        if (response.status === HttpStatus.NotFound) {
            storeChanged = this.tasksInMyDayStatusTable.set(this.currentUserId, StoreEntityStatus.NotFound) || storeChanged;
            return storeChanged;
        }
        if (response.status === HttpStatus.Forbidden) {
            storeChanged = this.tasksInMyDayStatusTable.set(this.currentUserId, StoreEntityStatus.Inaccessible) || storeChanged;
            return storeChanged;
        }
        return storeChanged;
    }
    handleFetchBasicTasksForMyDaySucceededAction(action) {
        const { taskEntitySets, isLastPage, accumulatedTaskIds } = action;
        let storeChanged = false;
        if (this.isTaskInMyDayFetchingInProgress[this.currentUserId]) {
            this.isTaskInMyDayFetchingInProgress[this.currentUserId] = false;
            storeChanged = true;
        }
        for (const taskEntitySet of taskEntitySets){
            // Insert the tasks into the store if non existent or update baseline if existent
            storeChanged = this.upsertTaskEntitySet(taskEntitySet) || storeChanged;
        }
        // Update the index status if there are any changes
        if (isLastPage) {
            storeChanged = this.deleteExtraneousTasks("myday", "true", accumulatedTaskIds, true) || storeChanged;
            storeChanged = this.tasksInMyDayStatusTable.set(this.currentUserId, StoreEntityStatus.Present) || storeChanged;
        }
        return storeChanged;
    }
    handleFetchBasicTasksForPlanAction(action) {
        if (this.isTaskInPlanFetchingInProgress[action.planId]) {
            return false;
        }
        this.isTaskInPlanFetchingInProgress[action.planId] = true;
        return true;
    }
    handleFetchBasicTasksForPlanFailedAction(action) {
        let storeChanged = false;
        const { response, planId } = action;
        if (this.isTaskInPlanFetchingInProgress[planId]) {
            this.isTaskInPlanFetchingInProgress[planId] = false;
            storeChanged = true;
        }
        // If no response or response is not 404 or 403, set status to UnableToFetch
        if (!response || response.status !== HttpStatus.NotFound && response.status !== HttpStatus.Forbidden) {
            storeChanged = this.tasksInPlanStatusTable.set(planId, StoreEntityStatus.UnableToFetch) || storeChanged;
            return storeChanged;
        }
        if (response.status === HttpStatus.NotFound) {
            storeChanged = this.tasksInPlanStatusTable.set(planId, StoreEntityStatus.NotFound) || storeChanged;
            return storeChanged;
        }
        if (response.status === HttpStatus.Forbidden) {
            storeChanged = this.tasksInPlanStatusTable.set(planId, StoreEntityStatus.Inaccessible) || storeChanged;
            return storeChanged;
        }
        return storeChanged;
    }
    handleFetchBasicTasksForPlanSucceededAction(action) {
        const { planId, taskEntitySets, isLastPage, accumulatedTaskIds } = action;
        let storeChanged = false;
        if (this.isTaskInPlanFetchingInProgress[planId]) {
            this.isTaskInPlanFetchingInProgress[planId] = false;
            storeChanged = true;
        }
        for (const taskEntitySet of taskEntitySets){
            storeChanged = this.upsertTaskEntitySet(taskEntitySet) || storeChanged;
        }
        // Update the plan index status if there are any changes
        if (isLastPage) {
            storeChanged = this.deleteExtraneousTasks("plan", planId, accumulatedTaskIds, false) || storeChanged;
            storeChanged = this.tasksInPlanStatusTable.set(planId, StoreEntityStatus.Present) || storeChanged;
        }
        return storeChanged;
    }
    handleFetchBasicTasksForTodoPlanFinalizedAction(action) {
        const { planId, succeeded } = action;
        let storeChanged = false;
        // Update the plan index status if there are any changes
        if (succeeded) {
            storeChanged = this.tasksInPlanStatusTable.set(planId, StoreEntityStatus.Present);
        }
        if (!succeeded) {
            storeChanged = this.tasksInPlanStatusTable.set(planId, StoreEntityStatus.UnableToFetch);
        }
        return storeChanged;
    }
    handleCreateBasicTaskAction(action) {
        const { taskEntitySet } = action;
        this.addTaskAndStatus(taskEntitySet);
        return true;
    }
    handleCreateBasicTaskFailedAction(action) {
        const { taskEntitySet, response } = action;
        const { task } = taskEntitySet;
        const storeCopy = this.taskTable.get(task.id);
        if (!storeCopy) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e2495c0 /* tag_4jjxa */ , TraceLevel.Warning, `TaskNotFoundInStore [Action=${action.type}][TaskId=${task.id}]`);
            return false;
        }
        // Remove from the store tables
        this.deleteTaskAndStatus(storeCopy.baseline.id, true);
        // Update the status if appropriate
        if (response != null) {
            return this.updateStatusBasedOnResponse(this.taskStatusTable, task.id, response);
        }
        return true;
    }
    handleCreateBasicTaskSucceededAction(action) {
        const { taskEntitySet, localId } = action;
        const { task, taskDetails, taskFormatData } = taskEntitySet;
        const storeCopy = this.taskTable.get(localId);
        if (!storeCopy) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e2495a3 /* tag_4jjw9 */ , TraceLevel.Warning, `TaskNotFoundInStore [Action=${action.type}][LocalId=${localId}]`);
            return false;
        }
        storeCopy.updateBaseline(task);
        this.taskTable.reKey(localId, task.id);
        this.deleteTaskAndStatus(localId, false);
        this.taskStatusTable.set(task.id, StoreEntityStatus.Present);
        const taskDetailsStoreCopy = this.taskDetailsTable.get(localId);
        if (taskDetails && taskDetailsStoreCopy) {
            taskDetailsStoreCopy.updateBaseline(taskDetails);
            this.taskDetailsTable.reKey(localId, task.id);
        }
        const assignedToFormatDataStoreCopy = this.taskboardTaskFormatForAssignedToTable.get(localId);
        if (taskFormatData?.assignmentBoardFormat && assignedToFormatDataStoreCopy) {
            assignedToFormatDataStoreCopy.updateBaseline(taskFormatData.assignmentBoardFormat);
            this.taskboardTaskFormatForAssignedToTable.reKey(localId, task.id);
        }
        const bucketFormatDataStoreCopy = this.taskboardTaskFormatForBucketTable.get(localId);
        if (taskFormatData?.bucketBoardFormat && bucketFormatDataStoreCopy) {
            bucketFormatDataStoreCopy.updateBaseline(taskFormatData.bucketBoardFormat);
            this.taskboardTaskFormatForBucketTable.reKey(localId, task.id);
        }
        const progressFormatDataStoreCopy = this.taskboardTaskFormatForProgressTable.get(localId);
        if (taskFormatData?.progressBoardFormat && progressFormatDataStoreCopy) {
            progressFormatDataStoreCopy.updateBaseline(taskFormatData.progressBoardFormat);
            this.taskboardTaskFormatForProgressTable.reKey(localId, task.id);
        }
        return true;
    }
    handleDeltaSyncCreateBasicTaskAction(action) {
        return this.upsertTaskEntitySet(action.taskEntitySet);
    }
    handleDeleteBasicTaskAction(action) {
        const taskId = action.task.id;
        const storeCopy = this.taskTable.get(taskId);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e2495a2 /* tag_4jjw8 */ , TraceLevel.Warning, `TaskNotFoundInStore [Action=${action.type}][TaskId=${taskId}]`);
            return false;
        }
        // No need to delete if already soft deleted.
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            this.loggers.traceLogger.logTrace(0x1e2495a1 /* tag_4jjw7 */ , TraceLevel.Warning, `StoreException: Basic task already soft deleted in store [TaskId=${taskId}]`);
            return false;
        }
        if (storeCopy) {
            // Soft delete the task
            storeCopy.softDelete();
            this.taskStatusTable.set(taskId, StoreEntityStatus.SoftDeleted);
            return true;
        }
        return false;
    }
    handleDeleteBasicTaskFailedAction(action) {
        const { response } = action;
        const taskId = action.task.id;
        const storeCopy = this.taskTable.get(taskId);
        if (!storeCopy) {
            /**
             * This can happen if a delta sync delete occurs before the
             * response of the delete returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e2495a0 /* tag_4jjw6 */ , TraceLevel.Warning, `TaskNotFoundInStore [Action=${action.type}][TaskId=${taskId}]`);
            return false;
        }
        let storeChanged = false;
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            storeCopy.undelete();
            this.taskStatusTable.set(taskId, StoreEntityStatus.Present);
            storeChanged = true;
        }
        // Update the status if appropriate
        if (response != null) {
            storeChanged = this.updateStatusBasedOnResponse(this.taskStatusTable, taskId, response) || storeChanged;
        }
        return storeChanged;
    }
    handleDeleteBasicTaskSucceededAction(action) {
        const { taskId } = action;
        const storeCopy = this.taskTable.get(taskId);
        if (storeCopy == null) {
            // Log this case. This can happen in the rare chance if a delta sync returns before the response of the delete
            const msg = `StoreException: Basic task not found in store. [TaskId=${taskId}]`;
            this.loggers.traceLogger.logTrace(0x1e24959f /* tag_4jjw5 */ , TraceLevel.Info, msg);
            return false;
        }
        // Remove item from the store
        this.deleteTaskAndStatus(taskId, true);
        return true;
    }
    handleDeltaSyncDeleteBasicTaskAction(action) {
        const taskId = action.task.id;
        // No-op if the task doesn't exist in the store
        const storeCopy = this.taskTable.get(taskId);
        if (storeCopy == null) {
            return false;
        }
        // Process similarly to optimistic delete flow
        this.handleDeleteBasicTaskAction(action);
        // Hard delete the task
        this.deleteTaskAndStatus(taskId, true);
        return true;
    }
    handleUpdateBasicTaskAction(action) {
        let storeChanged = false;
        const { originalTaskEntitySet, updatedTaskEntitySet, updateKey } = action;
        const taskId = originalTaskEntitySet.id;
        let updatedTask = updatedTaskEntitySet.task;
        const updatedTaskDetails = updatedTaskEntitySet.taskDetails;
        const updatedTaskFormatData = updatedTaskEntitySet.taskFormatData;
        const storeCopy = this.taskTable.get(taskId);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e24959e /* tag_4jjw4 */ , TraceLevel.Warning, `TaskNotFoundInStore [Action=${action.type}][TaskId=${taskId}]`);
            return false;
        }
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            return false;
        }
        if (updatedTask != null) {
            if (updatedTaskDetails != null) {
                const promotedProperties = updatedTaskDetails.getPromotedProperties(this.configProvider().changeGates.DisableTodoNotesQuickLookBlock);
                updatedTask = updatedTask.setPromotedProperties(promotedProperties);
            }
            const taskDelta = storeCopy.value.getDiff(updatedTask);
            if (!isEmpty(taskDelta)) {
                storeCopy.pushDelta(updatedTask, updateKey);
                storeChanged = true;
            }
        }
        if (updatedTaskDetails != null) {
            const storeDetailsCopy = this.taskDetailsTable.get(taskId);
            if (storeDetailsCopy) {
                storeDetailsCopy.pushDelta(updatedTaskDetails, updateKey);
                storeChanged = true;
            }
        }
        if (updatedTaskFormatData?.assignmentBoardFormat != null) {
            const storeAssignedToFormatCopy = this.taskboardTaskFormatForAssignedToTable.get(taskId);
            if (storeAssignedToFormatCopy) {
                storeAssignedToFormatCopy.pushDelta(updatedTaskFormatData.assignmentBoardFormat, updateKey);
                storeChanged = true;
            }
        }
        if (updatedTaskFormatData?.bucketBoardFormat != null) {
            const storeBucketFormatCopy = this.taskboardTaskFormatForBucketTable.get(taskId);
            if (storeBucketFormatCopy) {
                storeBucketFormatCopy.pushDelta(updatedTaskFormatData.bucketBoardFormat, updateKey);
                storeChanged = true;
            }
        }
        if (updatedTaskFormatData?.progressBoardFormat != null) {
            const storeProgressFormatCopy = this.taskboardTaskFormatForProgressTable.get(taskId);
            if (storeProgressFormatCopy) {
                storeProgressFormatCopy.pushDelta(updatedTaskFormatData.progressBoardFormat, updateKey);
                storeChanged = true;
            }
        }
        return storeChanged;
    }
    handleUpdateBasicTaskFailedAction(action) {
        let storeChanged = false;
        const { taskId, response, updateKey } = action;
        const storeCopy = this.taskTable.get(taskId);
        if (storeCopy == null || storeCopy.status === ExistenceStatus.SoftDeleted) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the update returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e24959d /* tag_4jjw3 */ , TraceLevel.Warning, `StoreException: Basic task not found in store [TaskId=${taskId}][ExistenceStatus=${storeCopy?.status ?? "Non-existent"}]`);
            return false;
        }
        // Revert the task delta if it exists
        if (storeCopy.getDelta(updateKey) != null) {
            storeCopy.revertDelta(updateKey);
            storeChanged = true;
        }
        // Enhanced failure handling - revert all entity deltas when flight is enabled
        if (this.configProvider().flights.EnableEnhancedTaskUpdateFailureHandling) {
            // Revert the taskDetails delta if it exists
            const storeDetailsCopy = this.taskDetailsTable.get(taskId);
            if (storeDetailsCopy?.getDelta(updateKey) != null) {
                storeDetailsCopy.revertDelta(updateKey);
                storeChanged = true;
            }
            // Revert the assignmentBoardFormat delta if it exists
            const storeAssignedToFormatCopy = this.taskboardTaskFormatForAssignedToTable.get(taskId);
            if (storeAssignedToFormatCopy?.getDelta(updateKey) != null) {
                storeAssignedToFormatCopy.revertDelta(updateKey);
                storeChanged = true;
            }
            // Revert the bucketBoardFormat delta if it exists
            const storeBucketFormatCopy = this.taskboardTaskFormatForBucketTable.get(taskId);
            if (storeBucketFormatCopy?.getDelta(updateKey) != null) {
                storeBucketFormatCopy.revertDelta(updateKey);
                storeChanged = true;
            }
            // Revert the progressBoardFormat delta if it exists
            const storeProgressFormatCopy = this.taskboardTaskFormatForProgressTable.get(taskId);
            if (storeProgressFormatCopy?.getDelta(updateKey) != null) {
                storeProgressFormatCopy.revertDelta(updateKey);
                storeChanged = true;
            }
        }
        // Update the status if necessary
        if (response != null) {
            storeChanged = this.updateStatusBasedOnResponse(this.taskStatusTable, taskId, response) || storeChanged;
        }
        return storeChanged;
    }
    handleUpdateBasicTaskSucceededAction(action) {
        let storeChanged = false;
        const { updatedTaskEntitySet, updateKey } = action;
        const taskId = updatedTaskEntitySet.id;
        let updatedTask = updatedTaskEntitySet.task;
        const updatedTaskDetails = updatedTaskEntitySet.taskDetails;
        const updatedTaskFormatData = updatedTaskEntitySet.taskFormatData;
        // Process task
        const storeCopy = this.taskTable.get(taskId);
        if (storeCopy == null || storeCopy.status === ExistenceStatus.SoftDeleted) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the update returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e24959c /* tag_4jjw2 */ , TraceLevel.Warning, `TaskNotFoundInStore [TaskId=${taskId}][ExistenceStatus=${storeCopy?.status ?? "Non-existent"}]`);
            return false;
        }
        if (updatedTaskDetails != null) {
            const promotedProperties = updatedTaskDetails.getPromotedProperties(this.configProvider().changeGates.DisableTodoNotesQuickLookBlock);
            updatedTask = updatedTask.setPromotedProperties(promotedProperties);
        }
        // Commit the pending update
        if (storeCopy.getDelta(updateKey) != null) {
            storeCopy.commitDelta(updateKey, updatedTask);
            storeChanged = true;
        }
        // Process task details
        const storeDetailsCopy = this.taskDetailsTable.get(taskId);
        if (updatedTaskDetails != null) {
            if (storeDetailsCopy != null) {
                if (storeDetailsCopy.getDelta(updateKey) != null) {
                    storeDetailsCopy.commitDelta(updateKey, updatedTaskDetails);
                    storeChanged = true;
                }
            } else {
                this.taskDetailsTable.add(taskId, updatedTaskDetails);
                storeChanged = true;
            }
        }
        // Process task format data
        // assignment task format
        const assignedToFormatDataStoreCopy = this.taskboardTaskFormatForAssignedToTable.get(taskId);
        if (updatedTaskFormatData?.assignmentBoardFormat) if (assignedToFormatDataStoreCopy != null) {
            if (assignedToFormatDataStoreCopy.getDelta(updateKey) != null) {
                assignedToFormatDataStoreCopy.commitDelta(updateKey, updatedTaskFormatData?.assignmentBoardFormat);
                storeChanged = true;
            }
        } else {
            this.taskboardTaskFormatForAssignedToTable.add(taskId, updatedTaskFormatData.assignmentBoardFormat);
            storeChanged = true;
        }
        // bucket task format
        const bucketFormatDataStoreCopy = this.taskboardTaskFormatForBucketTable.get(taskId);
        if (updatedTaskFormatData?.bucketBoardFormat) if (bucketFormatDataStoreCopy != null) {
            if (bucketFormatDataStoreCopy.getDelta(updateKey) != null) {
                bucketFormatDataStoreCopy.commitDelta(updateKey, updatedTaskFormatData?.bucketBoardFormat);
                storeChanged = true;
            }
        } else {
            this.taskboardTaskFormatForBucketTable.add(taskId, updatedTaskFormatData.bucketBoardFormat);
            storeChanged = true;
        }
        // progress task format
        const progressFormatDataStoreCopy = this.taskboardTaskFormatForProgressTable.get(taskId);
        if (updatedTaskFormatData?.progressBoardFormat) if (progressFormatDataStoreCopy != null) {
            if (progressFormatDataStoreCopy.getDelta(updateKey) != null) {
                progressFormatDataStoreCopy.commitDelta(updateKey, updatedTaskFormatData?.progressBoardFormat);
                storeChanged = true;
            }
        } else {
            this.taskboardTaskFormatForProgressTable.add(taskId, updatedTaskFormatData.progressBoardFormat);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleDeltaSyncUpdateBasicTaskAction(action) {
        let storeChanged = false;
        const { updatedTaskEntitySet } = action;
        const storeCopy = this.taskTable.get(updatedTaskEntitySet.id);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e24959b /* tag_4jjw1 */ , TraceLevel.Warning, `TaskNotFoundInStore [Action=${action.type}][TaskId=${updatedTaskEntitySet.task.id}]`);
            return false;
        }
        if (updatedTaskEntitySet.task?.internalProperties?.mars_planner_microsoft_CopilotProperties) {
            delete this.taskIdToPMWorkflowState[updatedTaskEntitySet.task.id];
        }
        storeChanged = this.upsertTaskEntitySet(updatedTaskEntitySet, false) || storeChanged;
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            // This is an indicator that the out going delete request will fail from a delete conflict. Log a warning and only update the baseline if needed
            this.loggers.traceLogger.logTrace(0x1e24959a /* tag_4jjw0 */ , TraceLevel.Warning, `StoreException: Basic task soft deleted in store [TaskId=${updatedTaskEntitySet.task.id}]`);
        }
        const updatedTaskDetails = updatedTaskEntitySet.taskDetails;
        if (updatedTaskDetails != null) {
            const storeDetailsCopy = this.taskDetailsTable.get(updatedTaskEntitySet.id);
            // Check whether this detail change is about approval. If yes, we set "InProgress" status back to false because the update has been finished.
            if (storeDetailsCopy && storeDetailsCopy.baseline.approval?.approvalStatus !== updatedTaskDetails.approval?.approvalStatus && this.isApprovalOperationInProgress.get(updatedTaskEntitySet.id)) {
                // approval is updated, that is, the approval actions are finished, set loading back to false
                this.setIsApprovalOperationInProgress(updatedTaskEntitySet.id, false);
            }
        }
        return storeChanged;
    }
    handleUploadAttachmentForTaskAction(action) {
        const { taskId, attachment } = action;
        if (!this.inProgressUploadsByTask[taskId]) {
            this.inProgressUploadsByTask[taskId] = [];
        }
        this.inProgressUploadsByTask[taskId].push(attachment);
        return true;
    }
    handleUploadAttachmentForTaskFailedAction(action) {
        const { taskId, attachmentUrl } = action;
        // Revert optimistic update after upload attachment failure
        return this.revertInProgressUploadByTask(taskId, attachmentUrl);
    }
    handleUploadAttachmentForTaskSucceededAction(action) {
        const { taskId, attachmentUrl } = action;
        // Revert optimistic update after upload attachment success, because a separate UpdateTask flow handles the update of the real upload session
        return this.revertInProgressUploadByTask(taskId, attachmentUrl);
    }
    handleUndeleteBasicTaskAction(action) {
        const taskEntitySet = action.task;
        const taskId = taskEntitySet.id;
        const storeCopy = this.taskTable.get(taskId);
        if (storeCopy != null) {
            this.loggers.traceLogger.logTrace(0x1e249599 /* tag_4jjwz */ , TraceLevel.Warning, `Task is already in store [TaskId=${taskId}]`);
            return false;
        }
        if (taskEntitySet.taskDetails != null && this.taskDetailsTable.get(taskId)) {
            this.loggers.traceLogger.logTrace(0x1e249598 /* tag_4jjwy */ , TraceLevel.Warning, `TaskDetails is already in store [TaskId=${taskId}]`);
            return false;
        }
        this.addTaskAndStatus(taskEntitySet);
        return true;
    }
    handleUndeleteBasicTaskFailedAction(action) {
        const taskId = action.taskId;
        const storeCopy = this.taskTable.get(taskId);
        if (storeCopy == null) {
            this.loggers.traceLogger.logTrace(0x1e249597 /* tag_4jjwx */ , TraceLevel.Warning, `TaskNotFoundInStore [TaskId=${taskId}]`);
            return false;
        }
        // Revert optimistic update after undelete failure
        this.deleteTaskAndStatus(taskId, true);
        return true;
    }
    handleUndeleteBasicTaskSucceededAction(action) {
        const taskEntitySet = action.task;
        const taskId = taskEntitySet.id;
        const storeCopy = this.taskTable.get(taskId);
        if (storeCopy == null) {
            this.loggers.traceLogger.logTrace(0x1e249596 /* tag_4jjww */ , TraceLevel.Warning, `TaskNotFoundInStore [TaskId=${taskId}]`);
            return false;
        }
        storeCopy.updateBaseline(taskEntitySet.task);
        this.taskStatusTable.set(taskEntitySet.id, StoreEntityStatus.Present);
        if (taskEntitySet.taskDetails) {
            const storeTaskDetailsCopy = this.taskDetailsTable.get(taskId);
            if (storeTaskDetailsCopy) {
                storeTaskDetailsCopy.updateBaseline(taskEntitySet.taskDetails);
            } else {
                this.taskDetailsTable.add(taskId, taskEntitySet.taskDetails);
            }
        }
        const formats = taskEntitySet.taskFormatData;
        if (formats) {
            if (formats.assignmentBoardFormat) {
                const storeAssignedToFormatCopy = this.taskboardTaskFormatForAssignedToTable.get(taskId);
                if (storeAssignedToFormatCopy) {
                    storeAssignedToFormatCopy.updateBaseline(formats.assignmentBoardFormat);
                } else {
                    this.taskboardTaskFormatForAssignedToTable.add(taskId, formats.assignmentBoardFormat);
                }
            }
            if (formats.bucketBoardFormat) {
                const storeBucketFormatCopy = this.taskboardTaskFormatForBucketTable.get(taskId);
                if (storeBucketFormatCopy) {
                    storeBucketFormatCopy.updateBaseline(formats.bucketBoardFormat);
                } else {
                    this.taskboardTaskFormatForBucketTable.add(taskId, formats.bucketBoardFormat);
                }
            }
            if (formats.progressBoardFormat) {
                const storeProgressFormatCopy = this.taskboardTaskFormatForProgressTable.get(taskId);
                if (storeProgressFormatCopy) {
                    storeProgressFormatCopy.updateBaseline(formats.progressBoardFormat);
                } else {
                    this.taskboardTaskFormatForProgressTable.add(taskId, formats.progressBoardFormat);
                }
            }
        }
        return true;
    }
    handleUpdateBulkEditStatusAction(action) {
        if (this.isBulkEditOngoing !== action.isBulkEditInProgress) {
            this.isBulkEditOngoing = action.isBulkEditInProgress;
            return true;
        }
        return false;
    }
    //#endregion handlers
    //#region approval details
    /**
     * fetch the approval detail in the store
     * @param action The fetch approval details action
     */ handleFetchApprovalDetailsAction(action) {
        return this.taskApprovalDetailsStatusTable.set(action.taskId, StoreEntityStatus.Fetching);
    }
    /**
     * Succeeded to fetch the approval detail in the store, save it into store
     * @param action The fetch approval details succeeded action
     */ handleFetchApprovalDetailsSucceededAction(action) {
        const { taskId, approvalDetailResponse } = action;
        let storeChanged = false;
        if (!taskId || !approvalDetailResponse) {
            return storeChanged;
        }
        const storeCopy = this.taskApprovalDetailsTable.get(taskId);
        if (!isEqual(storeCopy, approvalDetailResponse)) {
            this.taskApprovalDetailsTable.set(taskId, approvalDetailResponse);
            storeChanged = true;
        }
        storeChanged = this.taskApprovalDetailsStatusTable.set(taskId, StoreEntityStatus.Present) || storeChanged;
        return storeChanged;
    }
    /**
     * Failed to fetch the approval detail in the store, set the status to failed
     * @param action The fetch approval details failed action
     */ handleFetchApprovalDetailsFailedAction(action) {
        let storeChanged = false;
        const { response, taskId } = action;
        // Update the status if appropriate
        if (response?.status === HttpStatus.NotFound || response?.status === HttpStatus.Forbidden) {
            storeChanged = this.updateStatusBasedOnResponse(this.taskApprovalDetailsStatusTable, taskId, response);
        } else {
            this.taskApprovalDetailsStatusTable.set(taskId, StoreEntityStatus.UnableToFetch);
            storeChanged = true;
        }
        return storeChanged;
    }
    /**
     * Create/Update/Cancel the approval detail in the store, could be the action or the failed action, set the approval operation in progress to be true
     * @param action The approval details action which could be create/update/cancel
     */ handleTaskApprovalDetailsAction(action) {
        return this.setIsApprovalOperationInProgress(action.taskId, true);
    }
    /**
     * Failed to create/update/cancel the approval detail in the store, set the approval operation in progress back to be false
     * @param action The approval details failed action which could be create/update/cancel
     */ handleTaskApprovalDetailsActionFailure(action) {
        return this.setIsApprovalOperationInProgress(action.taskId, false);
    }
    /**
     * Set isApprovalOperationInProgress into the boolean we want. If it is progressing, the approval component will show Shimmer.
     * @param taskId The task ID we are handling
     * @param inProgress Is the operation in progress
     */ setIsApprovalOperationInProgress(taskId, inProgress) {
        if (taskId == null || taskId === "") {
            const msg = `StoreException: taskId is null or empty [TaskId=${taskId}]`;
            this.loggers.traceLogger.logTrace(0x1e249595 /* tag_4jjwv */ , TraceLevel.Error, msg);
            return false;
        }
        return this.isApprovalOperationInProgress.set(taskId, inProgress);
    }
    //#endregion approval details
    //#region conversations
    handleCreateConversationThreadAction(action) {
        let storeChanged = false;
        const { threadId, task } = action;
        const taskId = task.id;
        const storeCopy = this.taskTable.get(task.id);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e249594 /* tag_4jjwu */ , TraceLevel.Warning, `TaskNotFoundInStore [Action=${action.type}][TaskId=${taskId}]`);
            return false;
        }
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            this.loggers.traceLogger.logTrace(0x1e249593 /* tag_4jjwt */ , TraceLevel.Warning, `StoreException: Basic task soft deleted in store [TaskId=${taskId}]`);
            return false;
        }
        const taskDelta = storeCopy.value.getDiff(task);
        if (!isEmpty(taskDelta)) {
            storeCopy.pushDelta(task, threadId);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleCreateConversationThreadSucceededAction(action) {
        let storeChanged = false;
        const { localThreadId, task } = action;
        const taskId = task.id;
        const storeCopy = this.taskTable.get(task.id);
        if (!storeCopy) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the update returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e249592 /* tag_4jjws */ , TraceLevel.Warning, `TaskNotFoundInStore [Action=${action.type}][TaskId=${taskId}]`);
            return false;
        }
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            this.loggers.traceLogger.logTrace(0x1e249591 /* tag_4jjwr */ , TraceLevel.Warning, `StoreException: Basic task soft deleted in store [TaskId=${taskId}]`);
            return false;
        }
        if (storeCopy.getDelta(localThreadId) != null) {
            storeCopy.commitDelta(localThreadId, task);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleCopilotWorkflowUnifiedStateUpdateAction(action) {
        this.taskIdToPMWorkflowState[action.taskId] = action.workflowState;
        return true;
    }
    handleFetchLoopPageUrlForTaskAction(action) {
        return this.loopPageUrlStatusTable.set(action.taskId, StoreEntityStatus.Fetching);
    }
    handleFetchLoopPageUrlForTaskSucceededAction(action) {
        const { taskId, loopPageUrl } = action;
        this.loopPageUrlForTaskTable.set(taskId, loopPageUrl);
        this.loopPageUrlStatusTable.set(taskId, StoreEntityStatus.Present);
        return true;
    }
    handleFetchLoopPageUrlForTaskFailedAction(action) {
        const { taskId, response } = action;
        let storeChanged = false;
        // Update the status if appropriate
        if (response?.status === HttpStatus.NotFound || response?.status === HttpStatus.Forbidden) {
            storeChanged = this.updateStatusBasedOnResponse(this.loopPageUrlStatusTable, taskId, response);
        } else {
            storeChanged = this.loopPageUrlStatusTable.set(taskId, StoreEntityStatus.UnableToFetch);
        }
        return storeChanged;
    }
    handleDeleteLoopPageUrlForTaskAction(action) {
        const { taskId } = action;
        this.loopPageUrlForTaskTable.delete(taskId);
        this.loopPageUrlStatusTable.delete(taskId);
        return true;
    }
    handleCreateConversationThreadFailedAction(action) {
        let storeChanged = false;
        const { threadId, task, response } = action;
        const taskId = task.id;
        const storeCopy = this.taskTable.get(taskId);
        if (storeCopy == null || storeCopy.status === ExistenceStatus.SoftDeleted) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the update returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e249590 /* tag_4jjwq */ , TraceLevel.Warning, `TaskNotFoundInStore [TaskId=${taskId}][ExistenceStatus=${storeCopy?.status ?? "Non-existent"}]`);
            return false;
        }
        // Revert the delta if it exists
        if (storeCopy.getDelta(threadId) != null) {
            storeCopy.revertDelta(threadId);
            storeChanged = true;
        }
        // Update the status if necessary
        if (response != null) {
            storeChanged = this.updateStatusBasedOnResponse(this.taskStatusTable, taskId, response) || storeChanged;
        }
        return storeChanged;
    }
    //#endregion conversations
    //#region common methods
    /**
     * Add a task to the store tables
     * @param task basic task to add
     */ addTaskAndStatus(taskEntitySet) {
        this.taskTable.add(taskEntitySet.id, taskEntitySet.task);
        this.taskStatusTable.set(taskEntitySet.id, StoreEntityStatus.Present);
        if (taskEntitySet.taskDetails) {
            this.taskDetailsTable.add(taskEntitySet.id, taskEntitySet.taskDetails);
        }
        const formats = taskEntitySet.taskFormatData;
        if (formats) {
            if (formats.assignmentBoardFormat) {
                this.taskboardTaskFormatForAssignedToTable.add(taskEntitySet.id, formats.assignmentBoardFormat);
            }
            if (formats.bucketBoardFormat) {
                this.taskboardTaskFormatForBucketTable.add(taskEntitySet.id, formats.bucketBoardFormat);
            }
            if (formats.progressBoardFormat) {
                this.taskboardTaskFormatForProgressTable.add(taskEntitySet.id, formats.progressBoardFormat);
            }
        }
    }
    /**
     * Delete a task from the store tables
     * @param taskId Id of the basic task
     * @param deleteEntitySet delete the entire entity set or not
     */ deleteTaskAndStatus(taskId, deleteEntitySet) {
        this.taskTable.delete(taskId);
        this.taskStatusTable.delete(taskId);
        if (deleteEntitySet) {
            this.taskDetailsTable.delete(taskId);
            this.taskboardTaskFormatForAssignedToTable.delete(taskId);
            this.taskboardTaskFormatForBucketTable.delete(taskId);
            this.taskboardTaskFormatForProgressTable.delete(taskId);
        }
    }
    /**
     * Update the status of a store entity based on the request response
     * @param table The table to update
     * @param id Id of the store entity
     * @param response Response from the request
     */ updateStatusBasedOnResponse(table, id, response) {
        const status = getStatusBasedOnResponse(response);
        if (status) {
            return table.set(id, status);
        }
        return false;
    }
    /**
     * Upsert a task entity set into the store
     * @param taskEntitySet ITaskEntitySet - task entity set to be upserted
     * @param status markPresent - Set the status of the task as Present
     */ upsertTaskEntitySet(taskEntitySet, markPresent = true) {
        const task = taskEntitySet.task;
        let storeChanged = false;
        if (this.isTaskFetchingInProgress[task.id]) {
            this.isTaskFetchingInProgress[task.id] = false;
            storeChanged = true;
        }
        storeChanged = upsertVersionedBaseline(this.taskTable, task.id, task) || storeChanged;
        if (markPresent) {
            storeChanged = this.taskStatusTable.set(task.id, StoreEntityStatus.Present) || storeChanged;
        }
        if (taskEntitySet.taskDetails) {
            storeChanged = upsertVersionedBaseline(this.taskDetailsTable, task.id, taskEntitySet.taskDetails) || storeChanged;
        }
        if (taskEntitySet.taskFormatData?.assignmentBoardFormat) {
            storeChanged = upsertVersionedBaseline(this.taskboardTaskFormatForAssignedToTable, task.id, taskEntitySet.taskFormatData.assignmentBoardFormat) || storeChanged;
        }
        if (taskEntitySet.taskFormatData?.bucketBoardFormat) {
            storeChanged = upsertVersionedBaseline(this.taskboardTaskFormatForBucketTable, task.id, taskEntitySet.taskFormatData.bucketBoardFormat) || storeChanged;
        }
        if (taskEntitySet.taskFormatData?.progressBoardFormat) {
            storeChanged = upsertVersionedBaseline(this.taskboardTaskFormatForProgressTable, task.id, taskEntitySet.taskFormatData.progressBoardFormat) || storeChanged;
        }
        return storeChanged;
    }
    /**
     * Delete extraneous tasks from a collection index
     * It's possible that a task that is indexed to a particular collection is no longer a part of that collection.
     * This ensures that we clean them up when we have the latest info for that collection.
     * @param indexName index name
     * @param indexValue index value
     * @param accumulatedTaskIds accumulated tasks ids across all pages of response
     * @param isCompositeCollection  Collections that can contain data from ToDo
     */ deleteExtraneousTasks(indexName, indexValue, accumulatedTaskIds, isCompositeCollection) {
        let storeChanged = false;
        if (this.configProvider().flights.EnableTasksDataPersistence) {
            if (accumulatedTaskIds === null) {
                return storeChanged;
            }
            const taskIdsInIndex = Object.values(this.taskTable.getByIndex({
                name: indexName,
                value: indexValue
            })).map((task)=>task.baseline.id);
            const accumulatedTaskIdSet = new Set(accumulatedTaskIds);
            let toDoTaskIdSet = new Set();
            if (isCompositeCollection) {
                toDoTaskIdSet = new Set(taskIdsInIndex.filter((taskId)=>this.taskTable.get(taskId)?.value.kind === TaskKind.Todo));
            }
            for (const taskId of taskIdsInIndex){
                // We need to preserve following tasks in the final index-
                //  1. The latest accumulated set of tasks via the service
                //  2. The todo tasks, as they are fetched from a different service
                //  3. Local temporary tasks as they are awaiting in-flight requests
                if (!accumulatedTaskIdSet.has(taskId) && !LocalIdGenerator.isLocalTempId(taskId) && !toDoTaskIdSet.has(taskId)) {
                    storeChanged = [
                        this.taskTable.delete(taskId),
                        this.taskDetailsTable.delete(taskId),
                        this.taskboardTaskFormatForAssignedToTable.delete(taskId),
                        this.taskboardTaskFormatForBucketTable.delete(taskId),
                        this.taskboardTaskFormatForProgressTable.delete(taskId)
                    ].some(Boolean) || storeChanged;
                }
            }
        }
        return storeChanged;
    }
    /**
     * Reverts the in progress upload for a task
     * @param taskId
     * @param attachmentUrl
     */ revertInProgressUploadByTask(taskId, attachmentUrl) {
        if (!this.inProgressUploadsByTask[taskId].some((attachment)=>attachment.url === attachmentUrl)) {
            return false;
        }
        this.inProgressUploadsByTask[taskId] = this.inProgressUploadsByTask[taskId].filter((attachment)=>attachment.url !== attachmentUrl);
        return true;
    }
    constructor(configProvider, loggers){
        super(), this.configProvider = configProvider, this.loggers = loggers, this.currentUserId = MyDayUser.CURRENT_USER, this.taskTable = new EntityTable(StoreTableNames.Tasks, {
            plan: (task)=>task.planId,
            assignee: (task)=>task.assignments,
            myday: (task)=>task.isOnMyDay ? "true" : undefined,
            bucket: (task)=>task.bucketId,
            // Currently, we support only one goal per task, while the graph supports multiple goals.
            goal: (task)=>task.goalIds[0]
        }, new TasksSerializationHandler()), this.loopPageUrlForTaskTable = new BasicTable(StoreTableNames.LoopPageUrlForTask), this.loopPageUrlStatusTable = new BasicTable(StoreTableNames.LoopPageUrlStatus), this.taskDetailsTable = new EntityTable(StoreTableNames.TaskDetails, {}, new TaskDetailsSerializationHandler()), this.taskApprovalDetailsTable = new BasicTable(StoreTableNames.TaskApprovalDetails), this.taskboardTaskFormatForAssignedToTable = new EntityTable(StoreTableNames.TaskboardTaskFormatForAssignedTo, {}, new TaskBoardFormatAssignedToSerializationHandler()), this.taskboardTaskFormatForBucketTable = new EntityTable(StoreTableNames.TaskboardTaskFormatForBucket, {}, new TaskBoardFormatBucketSerializationHandler()), this.taskboardTaskFormatForProgressTable = new EntityTable(StoreTableNames.TaskboardTaskFormatForProgress, {}, new TaskBoardFormatProgressSerializationHandler()), this.isApprovalOperationInProgress = new BasicTable(StoreTableNames.IsApprovalOperationInProgress), this.taskApprovalDetailsStatusTable = new BasicTable(StoreTableNames.TaskApprovalDetailsStatus), this.taskStatusTable = new BasicTable(StoreTableNames.TaskStatus), this.tasksInPlanStatusTable = new BasicTable(StoreTableNames.TasksInPlanStatus, {}, new StoreEntityStatusSerializationHandler()), this.tasksByAssigneeStatusTable = new BasicTable(StoreTableNames.TasksByAssigneeStatus, {}, new StoreEntityStatusSerializationHandler()), this.tasksInMyDayStatusTable = new BasicTable(StoreTableNames.TasksInMyDayStatusTable, {}, new StoreEntityStatusSerializationHandler()), this.taskIdToPMWorkflowState = {}, this.isBulkEditOngoing = false, this.inProgressUploadsByTask = {}, this.isTaskFetchingInProgress = {}, this.isTaskInPlanFetchingInProgress = {}, this.isTaskInMyDayFetchingInProgress = {}, this.isTaskByAssigneeFetchingInProgress = {};
    }
}
